// Pure discount math shared by the server apply path and the drawer preview so a
// discount's persisted amount and its previewed amount can never drift.
export interface ComputeDiscountOffInput {
  discountType: 'percent' | 'fixed' | null;
  discountValue: number | null;
  base: number; // original charge the % computes off (never compounded onto another discount)
  netRemaining: number; // cap so booking_total can never fall below 0
}

// Returns the positive amount to subtract (>= 0); callers negate it for storage/preview.
export function computeDiscountOff({
  discountType,
  discountValue,
  base,
  netRemaining,
}: ComputeDiscountOffInput): number {
  if (!discountType || discountValue == null) return 0;
  if (base <= 0 || netRemaining <= 0) return 0;

  // Keep half-cent discounts from rounding down due to binary floating point.
  const percentCents = base * discountValue;
  const offOriginal =
    discountType === 'percent'
      ? Math.round(percentCents + Number.EPSILON * percentCents) / 100
      : Math.min(discountValue, base);

  return Math.min(offOriginal, netRemaining);
}

export interface StackDiscountInput {
  discountType: 'percent' | 'fixed' | null;
  discountValue: number | null;
  legacyPrice?: number; // stored (usually negative) price for legacy flat discounts
}

// Fold a list of discount units against a shared base, threading the remaining cap
// so the SUM of every stacked off can never exceed netRemaining (booking_total
// floors at 0 — a percent + a fixed no longer go negative). Returns positive offs
// in input order (callers negate). MUST mirror the server apply loop so preview ==
// persist. A percent still computes off `base` (the original); only the cap shrinks.
export function computeStackedDiscountOffs(
  discounts: StackDiscountInput[],
  base: number,
  netRemaining: number,
): number[] {
  let remaining = Math.max(0, netRemaining);
  return discounts.map((d) => {
    const off =
      !d.discountType || d.discountValue == null
        ? Math.min(Math.max(0, -(d.legacyPrice ?? 0)), remaining)
        : computeDiscountOff({
            discountType: d.discountType,
            discountValue: d.discountValue,
            base,
            netRemaining: remaining,
          });
    remaining = Math.max(0, remaining - off);
    return off;
  });
}
